/**
 * Shared Jitsi toolbar configuration.
 *
 * The curated `TOOLBAR_BUTTONS` list is the single source of truth for which
 * controls a meeting embed exposes. Both the authenticated in-app call
 * (`Call.tsx`) and the public guest-join embed (`GuestMeetPage.tsx`) use it so
 * guests get the SAME curated toolbar instead of Jitsi's default permissive one.
 *
 * "participants-pane" is intentionally dropped: the Orion moderation panel is the
 * one roster/moderation surface, so we don't ship two overlapping ones (design
 * doc §2). The replacement "Moderation" button is injected as a `customToolbarButtons`
 * entry (see `buildOrionCustomToolbarButtons` below).
 *
 * "chat" is intentionally dropped: the Orion chat side panel (see
 * `OrionChatPanel.tsx`) replaces Jitsi's built-in chat so users don't get two
 * chats (design doc §4). The replacement "Chat" button is likewise injected as a
 * `customToolbarButtons` entry.
 *
 * NOTE ON `customToolbarButtons`: contrary to the earlier belief (from a stale
 * reading of jitsi-meet#13769) that custom buttons are silently ignored through the
 * iframe config, they are in fact supported. Verified against the DEPLOYED bundle at
 * meet.staging.orionmessenger.io (build v=9268): `customToolbarButtons` is appended to
 * the config whitelist when the app runs embedded (`window.self !== window.top`), so it
 * IS accepted via the iframe `configOverwrite` (a `config` key, NOT `interfaceConfig`).
 * A custom-button click emits the external-API `toolbar-button-clicked` event carrying
 * the button `key` (surfaced to the parent as `toolbarButtonClicked`), which is how
 * `useOrionChatButtonEvent` / `useModerationButtonEvent` toggle their panels. No image
 * rebuild is required — this is pure ow2-chat-fe iframe config.
 */
/**
 * RECORDING (2026-07, Task D1): `recording` is intentionally KEPT in the curated list to surface
 * Jitsi's native file-recording control. Jitsi claim-gates this button so it renders ONLY for a
 * MODERATOR whose conference JWT carries `context.features.recording === "true"` (minted by the BE,
 * ow2-chat-be#229) AND when the served web config sets `config.recordingService.enabled = true` (a
 * SEPARATE infra change, not in this repo). Non-moderators — including ALL guests (`GuestMeetPage`),
 * who are never moderators — never see it, so no per-role FE gating is needed here. Until BOTH
 * deployment dependencies are live the button simply does not render; adding the id is inert and safe.
 * (Verified a valid TOOLBAR_BUTTONS id against the deployed bundle v=9268 + stable/jitsi-meet_11031.)
 *
 * DECLUTTER (2026-07): the following were removed from the curated list because they are
 * clutter / non-functional on the Orion deployment and only confuse users:
 *   - `download`       — "download the desktop app" link, irrelevant to the embed.
 *   - `embedmeeting`   — shows an <iframe> embed snippet; not a product we expose.
 *   - `etherpad`       — shared collaborative doc; the Etherpad service is not configured.
 *   - `sharedvideo`    — "share a YouTube video" into the call; out of scope.
 *   - `shareaudio`     — share-audio-only screenshare; redundant with `desktop`.
 *   - `backgroundblur` / `videobackgroundblur` — NEITHER is a valid button id in the deployed image;
 *     the real virtual-background control is `select-background` (kept below). The old blur ids were
 *     silently-ignored no-ops (no button rendered), so `backgroundblur` was swapped for
 *     `select-background` and `videobackgroundblur` dropped.
 *   - `filmstrip` / `fodeviceselection` — also NOT valid `TOOLBAR_BUTTONS` ids (absent from the
 *     button registry; `filmstrip` is an internal feature module, not a toolbar button). Silently
 *     ignored no-ops, so dropped.
 * If you re-add any of these, first confirm the id is still a valid jitsi button name for the
 * DEPLOYED image (see version-bump-todo.md item "TOOLBAR_BUTTONS valid names") — invalid names
 * are silently ignored by jitsi, so a stale/renamed id fails quietly.
 *
 * Every id below was cross-checked (2026-07) against BOTH the deployed bundle
 * (meet.staging.orionmessenger.io `app.bundle.min.js?v=9268`) and the pinned source
 * (`react/features/toolbox/constants.ts` `TOOLBAR_BUTTONS` at `stable/jitsi-meet_11031`).
 */
export const MEET_TOOLBAR_BUTTONS = [
  "camera",
  "closedcaptions",
  "desktop",
  "fullscreen",
  "hangup",
  "microphone",
  "raisehand",
  // Native file-recording control — claim-gated by Jitsi to moderators only (Task D1). See the
  // RECORDING note above for the two deployment dependencies that make it actually render.
  "recording",
  "security",
  "select-background",
  "settings",
  "shortcuts",
  "stats",
  "tileview",
  "videoquality",
] as const;

/**
 * IDs of the Orion `customToolbarButtons` entries. The click hooks
 * (`useOrionChatButtonEvent` / `useModerationButtonEvent`) match on these against the
 * `toolbarButtonClicked` event's `key`, so they are the single source of truth for both
 * the button config below and the listeners.
 */
export const ORION_CHAT_TOOLBAR_BUTTON_ID = "orion-chat";
export const ORION_MODERATION_TOOLBAR_BUTTON_ID = "orion-moderation";
export const ORION_MINIMIZE_TOOLBAR_BUTTON_ID = "orion-minimize";

/**
 * Jitsi renders a `customToolbarButtons` icon as an `<img src>`, so the icon must be a URL
 * or a `data:` URI. We inline self-contained SVGs (no network dependency) stroked in white
 * so they read on Jitsi's dark toolbar. Shapes mirror the lucide icons the old floating
 * overlay used (`MessageSquare` for chat, `ShieldCheck` for moderation) for visual continuity.
 * URL-encoded (not base64) so the source stays legible/diffable.
 */
function svgToDataUri(svg: string): string {
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

const CHAT_ICON_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>';

const MODERATION_ICON_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/><path d="m9 12 2 2 4-4"/></svg>';

// lucide `Minimize2` (two arrows pulling inward) — the same glyph the old floating overlay used.
const MINIMIZE_ICON_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/><line x1="14" x2="21" y1="10" y2="3"/><line x1="3" x2="10" y1="21" y2="14"/></svg>';

export const ORION_CHAT_TOOLBAR_BUTTON_ICON = svgToDataUri(CHAT_ICON_SVG);
export const ORION_MODERATION_TOOLBAR_BUTTON_ICON =
  svgToDataUri(MODERATION_ICON_SVG);
export const ORION_MINIMIZE_TOOLBAR_BUTTON_ICON =
  svgToDataUri(MINIMIZE_ICON_SVG);

/** A single `customToolbarButtons` entry, per the stable jitsi-meet shape. */
export type OrionCustomToolbarButton = {
  id: string;
  text: string;
  icon: string;
};

/**
 * Builds the `customToolbarButtons` array passed to the Jitsi iframe `configOverwrite`.
 *
 * Chat + minimize are always included. The moderation button is included by default
 * (`includeModeration` defaults true) so the INITIAL config — applied at conference setup before
 * the local role is known — always carries it; this is what makes the moderator-only behavior
 * regression-free: a participant who turns out to be a moderator can never lose the button even if
 * a later runtime config update fails, because it was there from the start.
 *
 * MODERATOR-ONLY VISIBILITY: custom toolbar buttons are global (no per-role flag on an entry). To
 * hide the moderation button from confirmed NON-moderators we re-apply `customToolbarButtons` at
 * runtime via the `overwriteConfig` command with `includeModeration:false` once the local role is
 * known to be non-moderator (see `useModerationButtonVisibility`). `overwriteConfig` re-rendering
 * custom toolbar buttons reactively in an iframe embed is verified against jitsi-meet source
 * (stable/jitsi-meet_11031) and the deployed bundle (v=9268). If that runtime update ever no-ops,
 * the failure degrades to the prior behavior — a non-moderator sees a "Moderation" button whose
 * click is already a no-op (`useModerationButtonEvent` gates on `localIsModerator`; `ModerationPanel`
 * self-gates) — never to a moderator losing access.
 *
 * Labels are passed in (rather than read here) so translation stays in the React layer.
 */
export function buildOrionCustomToolbarButtons(
  labels: {
    chat: string;
    moderation: string;
    minimize: string;
  },
  {
    includeModeration = true,
    includeChat = true,
    includeMinimize = true,
  }: {
    includeModeration?: boolean;
    includeChat?: boolean;
    /**
     * The STANDALONE meet page (Product B, `MeetJoinPage`) has no chat app
     * underneath the conference, so "minimize" is meaningless there — it opts
     * out with `false`. Defaults to `true` (in-chat call behavior unchanged).
     */
    includeMinimize?: boolean;
  } = {},
): OrionCustomToolbarButton[] {
  return [
    // Orion chat button — omitted when the in-call-chat feature flag is OFF
    // (`featureFlags.orionInCallChat`), in which case Jitsi's NATIVE chat button is
    // used instead (added to `MEET_TOOLBAR_BUTTONS` at the Call.tsx call site). The
    // `orion-chat` id is likewise swapped for native `chat` in the main-bar pin below.
    ...(includeChat
      ? [
          {
            id: ORION_CHAT_TOOLBAR_BUTTON_ID,
            text: labels.chat,
            icon: ORION_CHAT_TOOLBAR_BUTTON_ICON,
          },
        ]
      : []),
    // Moderation button — omitted for confirmed non-moderators (see doc above).
    ...(includeModeration
      ? [
          {
            id: ORION_MODERATION_TOOLBAR_BUTTON_ID,
            text: labels.moderation,
            icon: ORION_MODERATION_TOOLBAR_BUTTON_ICON,
          },
        ]
      : []),
    // Minimize lives in the toolbar (like chat + moderation). Its click collapses the iframe to
    // height:0 in Call.tsx, which hides this toolbar — so the RESTORE control stays an overlay
    // (the Maximize2 pill in CallButtons.tsx), it is not a custom toolbar button.
    // Omitted on the standalone meet page (see `includeMinimize` doc above).
    ...(includeMinimize
      ? [
          {
            id: ORION_MINIMIZE_TOOLBAR_BUTTON_ID,
            text: labels.minimize,
            icon: ORION_MINIMIZE_TOOLBAR_BUTTON_ICON,
          },
        ]
      : []),
  ];
}

/**
 * ────────────────────────────────────────────────────────────────────────────────────────────
 * `mainToolbarButtons` — PIN the Orion buttons in the MAIN control bar (demote natives to ⋯).
 * ────────────────────────────────────────────────────────────────────────────────────────────
 *
 * WHAT THIS DOES: passed as `configOverwrite.mainToolbarButtons` (a `config` key — it is in jitsi's
 * BASE config whitelist, so it is accepted through the iframe even when NOT embedded, unlike
 * `customToolbarButtons` which is embedded-only). Without it, our three custom buttons
 * (orion-chat / orion-moderation / orion-minimize) sort LAST and fall into the ⋯ overflow, while
 * `videoquality` / `fullscreen` / `security` occupy the main bar — the exact opposite of what we
 * want. This constant flips that: Orion buttons pinned in the main bar, those three natives demoted.
 *
 * ⚠️⚠️ FRAGILE — VERSION-COUPLED MECHANISM. RE-VERIFY ON EVERY orion-meet-web / jitsi IMAGE BUMP.
 * See `docs/jitsi/version-bump-todo.md` (item: "mainToolbarButtons placement"). Details:
 *
 * 1. KEYED BY BUTTON-COUNT, NOT BY WIDTH INDEX. Verified against the DEPLOYED bundle
 *    (meet.staging.orionmessenger.io `app.bundle.min.js?v=9268`). jitsi has an internal
 *    `THRESHOLDS` array of width buckets, each with a default `order` of a fixed LENGTH:
 *        width 675 → 10 buttons   width 470 → 6      width 225 → 3
 *        width 625 →  9           width 420 → 5      width 200 → 2
 *        width 565 →  8           width 370 → 4
 *        width 520 →  7
 *    The runtime indexes THIS config's inner arrays BY THEIR `.length` and overrides the default
 *    bucket whose button-count matches. So an 8-element array overrides the width-565 bucket, a
 *    7-element array overrides width-520, etc. The array `.length` — not its position — decides
 *    which width bucket it lands in, and how many buttons that bucket shows (jitsi slices the
 *    main bar to `order.length`). This is why the arrays below are ordered longest→shortest and
 *    must keep DISTINCT lengths (duplicate lengths collide in a Map; last wins).
 *
 * 2. We intentionally DO NOT supply 9- or 10-element arrays. Those two buckets (widths 625/675)
 *    are placeholder "dummy" entries in the default THRESHOLDS; with no override they are dropped,
 *    so the 8-button bucket (width 565) becomes the widest and applies to every desktop width — i.e.
 *    desktop shows exactly our 8-button main bar. Max 8 main buttons on desktop is the decluttered
 *    goal.
 *
 * 3. CUSTOM IDS ARE VALID HERE. jitsi merges `customToolbarButtons` into its button registry
 *    (group 4) AND appends their ids to the enabled-buttons set, so `orion-chat` etc. are legal
 *    entries in these `order` arrays and get placed in the main bar. (Only enabled+present ids are
 *    placed; a disabled/absent id is skipped and the freed slot is back-filled from jitsi's
 *    priority list — that is the only path by which a demoted native could sneak back in, hence the
 *    live-call check below.)
 *
 * 4. GRACEFUL DEGRADATION. `stable-11031` DOES consume `config.mainToolbarButtons` (verified in
 *    source: `toolbox/middleware.web.ts` dispatches `setMainToolbarThresholds(THRESHOLDS)` on
 *    SET/OVERWRITE_CONFIG, and `toolbox/actions.any.ts` `setMainToolbarThresholds` reads
 *    `config.mainToolbarButtons` and overrides each default THRESHOLD bucket by matching array
 *    `.length`); the deployed v=9268 bundle behaves identically. The safety property is GENERIC and
 *    version-independent: IF some future image ever stops consuming this key, our config becomes an
 *    inert no-op — the toolbar still works, buttons just fall back to the default THRESHOLDS ordering
 *    (Orion buttons land in overflow) — it NEVER breaks the toolbar. That is why we ship it even
 *    though the actual placement is only truly confirmable in a LIVE call at multiple window widths
 *    (there is no unit-testable render path for it). Re-verify consumption on every image bump.
 *
 * TARGET main bar (desktop, 8 slots): microphone · camera · desktop · raisehand · tileview ·
 * orion-chat · orion-moderation · orion-minimize · [hangup]. NOTE `hangup` is rendered by jitsi as
 * a dedicated always-visible button OUTSIDE this order machinery, so it is deliberately NOT listed
 * here. `settings` + the demoted `videoquality`/`fullscreen`/`security` live in ⋯ overflow.
 */
/**
 * Builds the `mainToolbarButtons` array-of-arrays.
 *
 * `chatButtonId` is the id that occupies the "chat" slot in every bucket:
 *   - `orion-chat` (default) when the Orion in-call chat flag is ON, and
 *   - native `"chat"` when the flag is OFF (`featureFlags.orionInCallChat === false`).
 *
 * WHY SUBSTITUTE (not remove) the chat slot when the flag is OFF: the bucket-selection
 * mechanism is keyed by array `.length` (see the long doc above), so removing an entry
 * would shift every bucket into the wrong width and collide lengths. Just as important,
 * jitsi back-fills any main-bar slot whose id is not an ENABLED button from its default
 * priority list — so if we left the (now-unregistered) `orion-chat` id here with the flag
 * OFF, a demoted native (videoquality/fullscreen/security) would reclaim that slot. Swapping
 * in the native `"chat"` id (which IS enabled via `MEET_TOOLBAR_BUTTONS` when the flag is OFF)
 * keeps every bucket length identical AND keeps the demoted natives in overflow.
 */
/**
 * `minimizeButtonId` follows the same substitute-don't-remove rule: the standalone
 * meet page (Product B, `MeetJoinPage`) doesn't register the `orion-minimize`
 * custom button, so leaving its id in these buckets would let jitsi back-fill the
 * slot with a demoted native. It substitutes a native id (e.g. `"fullscreen"`,
 * which IS enabled via `MEET_TOOLBAR_BUTTONS`) to keep every bucket length — the
 * width-bucket KEY — identical. Defaults to the Orion minimize id (in-chat call
 * behavior unchanged).
 */
export function buildOrionMainToolbarButtons(
  chatButtonId: string = ORION_CHAT_TOOLBAR_BUTTON_ID,
  minimizeButtonId: string = ORION_MINIMIZE_TOOLBAR_BUTTON_ID,
): string[][] {
  return [
    // 8 → width-565 bucket (widest live bucket → all desktop widths).
    [
      "microphone",
      "camera",
      "desktop",
      "raisehand",
      "tileview",
      chatButtonId,
      ORION_MODERATION_TOOLBAR_BUTTON_ID,
      minimizeButtonId,
    ],
    // 7 → width-520: drop tileview first.
    [
      "microphone",
      "camera",
      "desktop",
      "raisehand",
      chatButtonId,
      ORION_MODERATION_TOOLBAR_BUTTON_ID,
      minimizeButtonId,
    ],
    // 6 → width-470: drop raisehand.
    [
      "microphone",
      "camera",
      "desktop",
      chatButtonId,
      ORION_MODERATION_TOOLBAR_BUTTON_ID,
      minimizeButtonId,
    ],
    // 5 → width-420: drop desktop.
    [
      "microphone",
      "camera",
      chatButtonId,
      ORION_MODERATION_TOOLBAR_BUTTON_ID,
      minimizeButtonId,
    ],
    // 4 → width-370: keep chat + minimize (moderation → overflow).
    ["microphone", "camera", chatButtonId, minimizeButtonId],
    // 3 → width-225.
    ["microphone", "camera", chatButtonId],
    // 2 → width-200 (narrowest).
    ["microphone", "camera"],
  ];
}

/**
 * Default (Orion-chat-ON) main-bar pin. Kept as a named export for callers/tests that
 * assume the Orion chat button. `Call.tsx` builds the array via
 * `buildOrionMainToolbarButtons` so it can swap in native `"chat"` when the flag is OFF.
 */
export const ORION_MAIN_TOOLBAR_BUTTONS: string[][] =
  buildOrionMainToolbarButtons();

/**
 * The curated `TOOLBAR_BUTTONS` (interfaceConfig) allow-list for an embed.
 *
 * When `includeNativeChat` is true (Orion in-call chat flag OFF), Jitsi's native `chat`
 * button is appended so calls still have a chat surface. When false (flag ON), native chat
 * stays hidden and the Orion chat panel/button take over. `MEET_TOOLBAR_BUTTONS` itself
 * never lists `chat` — it is added here so the SSOT list stays flag-agnostic.
 */
export function buildMeetToolbarButtons(includeNativeChat: boolean): string[] {
  return includeNativeChat
    ? [...MEET_TOOLBAR_BUTTONS, "chat"]
    : [...MEET_TOOLBAR_BUTTONS];
}
